import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import { signIn as amplifySignIn, signOut as amplifySignOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { awsConfig } from '../config/aws-config';
import { initS3Client, destroyS3Client } from '../services/s3-upload';
import { autoSaveRegistry } from './useAutoSave';

// ── SSO Constants ──────────────────────────────────────────────────────
const COGNITO_DOMAIN = 'auth.easievaluation.com';
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || '4oumqt74055lsp699fnn286s7r';

// HIPAA §164.312(a)(2)(iii): Inactivity timeout enforced across browser restarts.
// Must match the session timeout in SecurityWrapper / phi-protection (7 minutes).
const INACTIVITY_TIMEOUT_MS = 7 * 60 * 1000;

// Configure Amplify — on localhost with empty config, checkAuth() will
// fail gracefully and show the login screen (which is what we want).
try {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: awsConfig.cognito.userPoolId,
        userPoolClientId: awsConfig.cognito.clientId,
        identityPoolId: awsConfig.cognito.identityPoolId,
        loginWith: {
          oauth: {
            domain: COGNITO_DOMAIN,
            scopes: ['email', 'openid', 'profile'],
            redirectSignIn: [window.location.origin + '/'],
            redirectSignOut: [window.location.origin + '/login'],
            responseType: 'code',
          },
        },
      },
    },
  });
} catch {
  // Amplify config may fail with empty values on localhost — that's fine
}

interface User {
  userId: string;
  email: string;
  organizationId?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<string | undefined>;
  signOut: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // ── HIPAA inactivity gate ──────────────────────────────────────
      // localStorage tokens survive "clear cache and history" in all browsers.
      // Without this gate, stale tokens silently re-authenticate users who
      // closed the browser hours/days ago. The 30-day Cognito refresh token
      // would keep them authenticated indefinitely.
      //
      // easi_last_activity is updated by SecurityWrapper on every user
      // interaction (mouse, keyboard, touch). If missing or >7 min stale,
      // the session is dead — clear everything and force re-login.
      const lastActivity = localStorage.getItem('easi_last_activity');
      const sessionExpired = !lastActivity ||
        (Date.now() - parseInt(lastActivity, 10)) > INACTIVITY_TIMEOUT_MS;

      if (sessionExpired) {
        // Wipe all auth tokens — do NOT attempt silent refresh
        localStorage.removeItem('easi_id_token');
        localStorage.removeItem('easi_access_token');
        localStorage.removeItem('easi_refresh_token');
        localStorage.removeItem('easi_last_activity');
        // Clear Amplify-managed tokens (dynamic key names)
        const amplifyKeys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('CognitoIdentityServiceProvider.')) {
            amplifyKeys.push(key);
          }
        }
        amplifyKeys.forEach(k => localStorage.removeItem(k));
        setUser(null);
        setIsLoading(false);
        return;
      }

      // 1. Check for SSO tokens in localStorage first (survives page refresh)
      const ssoIdToken = localStorage.getItem('easi_id_token');
      const ssoAccessToken = localStorage.getItem('easi_access_token');
      if (ssoIdToken && ssoAccessToken) {
        try {
          const payload = JSON.parse(atob(ssoIdToken.split('.')[1]));
          const expiry = (payload.exp as number) * 1000;
          if (expiry > Date.now()) {
            // Token still valid — restore session without hitting Cognito
            initS3Client(ssoIdToken);
            setUser({
              userId: payload.sub || '',
              email: payload.email || '',
            });
            return;
          }
          // Token expired — try refresh below
        } catch {
          // Token parse failed — fall through
        }
      }

      // 2. Try refreshing with stored refresh_token
      const refreshToken = localStorage.getItem('easi_refresh_token');
      if (refreshToken) {
        try {
          const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
              grant_type: 'refresh_token',
              client_id: CLIENT_ID,
              refresh_token: refreshToken,
            }),
          });
          const tokens = await response.json();
          if (tokens.id_token) {
            localStorage.setItem('easi_id_token', tokens.id_token);
            localStorage.setItem('easi_access_token', tokens.access_token);
            if (tokens.refresh_token) {
              localStorage.setItem('easi_refresh_token', tokens.refresh_token);
            }
            const payload = JSON.parse(atob(tokens.id_token.split('.')[1]));
            initS3Client(tokens.id_token);
            setUser({ userId: payload.sub || '', email: payload.email || '' });
            return;
          }
        } catch (refreshErr: any) {
          // Distinguish network errors from genuine auth rejection.
          // Network errors (offline, DNS, timeout) should NOT destroy the session
          // — the SSO tokens may still be valid for API calls or the next refresh
          // attempt. Falling through to Amplify for SSO users always fails
          // (no Amplify session) → setUser(null) → ProtectedRoute unmounts
          // the entire component tree → all workflow state destroyed.
          const isNetworkError = refreshErr instanceof TypeError ||
            refreshErr?.message?.includes('Failed to fetch') ||
            refreshErr?.message?.includes('NetworkError') ||
            refreshErr?.message?.includes('Network request failed') ||
            refreshErr?.code === 'ERR_NETWORK' ||
            refreshErr?.code === 'ECONNABORTED';
          if (isNetworkError && ssoIdToken) {
            // Preserve existing session — tokens may be expired but the
            // refresh token is likely still valid. Next API call's 401
            // interceptor will retry the refresh with network-error awareness.
            console.warn('[EASI Auth] Network error during token refresh — preserving session');
            const payload = JSON.parse(atob(ssoIdToken.split('.')[1]));
            initS3Client(ssoIdToken);
            setUser({ userId: payload.sub || '', email: payload.email || '' });
            return;
          }
          // Non-network error (e.g. invalid_grant) — fall through to Amplify
        }
      }

      // 3. Fall back to Amplify session (email/password login).
      //    SSO users don't have an Amplify session — getCurrentUser() will throw,
      //    which is caught below. This is the correct path for SSO users whose
      //    refresh token was genuinely rejected (not a network error).
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      const idToken = session.tokens?.idToken?.toString();
      if (idToken) {
        localStorage.setItem('easi_id_token', idToken);
        initS3Client(idToken);
      }

      setUser({
        userId: currentUser.userId,
        email: currentUser.signInDetails?.loginId || '',
      });
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── OAuth callback: exchange authorization code for tokens ─────────
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const oauthError = urlParams.get('error_description') || urlParams.get('error');

      // Handle OAuth errors (e.g. PreSignUp trigger rejection)
      if (oauthError) {
        // Strip Cognito's "PreSignUp failed with error " prefix to show our human-friendly message
        let cleanError = decodeURIComponent(oauthError.replace(/\+/g, ' '));
        const prefixMatch = cleanError.match(/^PreSignUp failed with error\s+/i);
        if (prefixMatch) {
          cleanError = cleanError.slice(prefixMatch[0].length);
        }
        sessionStorage.setItem('easi_oauth_error', cleanError);
        // Clean URL and redirect to login
        window.history.replaceState({}, document.title, '/login');
        setIsLoading(false);
        return;
      }

      if (!code) {
        // No OAuth callback — check existing session
        checkAuth();
        return;
      }

      try {
        const redirectUri = window.location.origin + '/';
        const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: CLIENT_ID,
            code: code,
            redirect_uri: redirectUri,
          }),
        });

        const tokens = await response.json();

        if (tokens.error) {
          console.error('OAuth token exchange failed:', tokens.error);
          setIsLoading(false);
          return;
        }

        // Clear ALL old auth state before storing new tokens.
        // Prevents cross-account contamination when switching users on the same browser.
        clearAllAuthState();

        // Store tokens for API calls
        if (tokens.id_token) {
          localStorage.setItem('easi_id_token', tokens.id_token);
        }
        if (tokens.access_token) {
          localStorage.setItem('easi_access_token', tokens.access_token);
        }
        if (tokens.refresh_token) {
          localStorage.setItem('easi_refresh_token', tokens.refresh_token);
        }

        // Seed activity timestamp — checkAuth()'s inactivity gate requires this
        localStorage.setItem('easi_last_activity', Date.now().toString());

        // Parse user info from id_token JWT
        const payload = JSON.parse(atob(tokens.id_token.split('.')[1]));

        setUser({
          userId: payload.sub || '',
          email: payload.email || '',
        });

        if (tokens.id_token) {
          initS3Client(tokens.id_token);
        }

        // Clean the code from URL without reload
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (err) {
        console.error('OAuth callback error:', err);
        // Fall back to checking Amplify session
        await checkAuth();
      } finally {
        setIsLoading(false);
      }
    };

    handleOAuthCallback();
  }, [checkAuth]);

  /**
   * Refresh SSO tokens directly — bypasses the inactivity gate in checkAuth().
   * The proactive refresh must NEVER go through the inactivity gate because a
   * clinician watching a processing spinner is NOT idle — the system is working
   * on their behalf. The inactivity gate is only for browser restart scenarios.
   */
  const refreshTokenSilently = useCallback(async () => {
    // Try SSO refresh path (Google/Apple sign-in)
    const refreshToken = localStorage.getItem('easi_refresh_token');
    if (refreshToken) {
      try {
        const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'refresh_token',
            client_id: CLIENT_ID,
            refresh_token: refreshToken,
          }),
        });
        const tokens = await response.json();
        if (tokens.id_token) {
          localStorage.setItem('easi_id_token', tokens.id_token);
          localStorage.setItem('easi_access_token', tokens.access_token);
          if (tokens.refresh_token) {
            localStorage.setItem('easi_refresh_token', tokens.refresh_token);
          }
          const payload = JSON.parse(atob(tokens.id_token.split('.')[1]));
          initS3Client(tokens.id_token);
          setUser({ userId: payload.sub || '', email: payload.email || '' });
          return;
        }
      } catch {
        // Network error — ignore, will retry on next API call via 401 interceptor
      }
    }
    // Fall back to Amplify refresh (email/password users)
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString();
      if (idToken) {
        localStorage.setItem('easi_id_token', idToken);
        initS3Client(idToken);
      }
    } catch {
      // Refresh failed — ignore, will retry on next API call
    }
  }, []);

  // Proactively refresh SSO tokens 5 minutes before expiry.
  // Uses refreshTokenSilently() instead of checkAuth() to bypass the
  // inactivity gate — a clinician watching a processing spinner is not idle.
  useEffect(() => {
    if (!user) return;
    const ssoIdToken = localStorage.getItem('easi_id_token');
    if (!ssoIdToken) return;
    try {
      const payload = JSON.parse(atob(ssoIdToken.split('.')[1]));
      const expiresAt = (payload.exp as number) * 1000;
      const refreshIn = expiresAt - Date.now() - 5 * 60 * 1000; // 5 min before expiry
      if (refreshIn <= 0) {
        refreshTokenSilently();
        return;
      }
      const timer = setTimeout(() => refreshTokenSilently(), refreshIn);
      return () => clearTimeout(timer);
    } catch {
      // ignore parse errors
    }
  }, [user, refreshTokenSilently]);

  /**
   * Clear ALL auth tokens from localStorage/sessionStorage.
   * MUST be called before any new sign-in to prevent cross-account contamination.
   * Without this, tokens from a previous session could cause User A to see User B's data.
   */
  function clearAllAuthState() {
    // Clear SSO tokens + activity timestamp
    localStorage.removeItem('easi_id_token');
    localStorage.removeItem('easi_access_token');
    localStorage.removeItem('easi_refresh_token');
    localStorage.removeItem('easi_last_activity');
    localStorage.removeItem('easi_remember_me');
    // Clear clinician-specific data that could leak across accounts
    localStorage.removeItem('easi_signature_block');
    // Clear ALL sessionStorage — patient data, views, transcripts, agreement status
    sessionStorage.clear();
    // Clear Amplify/Cognito SDK tokens (dynamic keys)
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('CognitoIdentityServiceProvider.')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
  }

  async function signIn(email: string, password: string): Promise<string | undefined> {
    // Clear stale tokens from any previous session to prevent cross-account data leakage
    clearAllAuthState();
    const result = await amplifySignIn({ username: email, password });

    if (result.isSignedIn) {
      // Seed activity timestamp so checkAuth()'s inactivity gate passes
      localStorage.setItem('easi_last_activity', Date.now().toString());
      await checkAuth();
      return undefined;
    }

    // Surface challenge step to caller (e.g. NEW_PASSWORD_REQUIRED)
    return result.nextStep?.signInStep;
  }

  async function signOut() {
    // Flush all pending auto-saves before destroying the session (max 3s)
    await autoSaveRegistry.flushAll();
    // Clear ALL auth state (SSO tokens, Amplify tokens, session caches)
    clearAllAuthState();
    // Destroy S3 client to prevent stale credential reuse across accounts
    destroyS3Client();

    try {
      // Global sign out revokes the refresh token on the Cognito server
      // so stolen tokens cannot be reused (HIPAA §164.312(a)(2)(iii))
      await amplifySignOut({ global: true });
    } catch {
      // May fail on localhost with no Cognito — that's fine
    }
    setUser(null);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        signIn,
        signOut,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
